import React, { useState, useEffect, useCallback } from 'react';
import {
  Tabs, Table, Button, Space, Modal, Form, Input, Switch, message,
  Typography, Tag, Checkbox, Spin, Select, Popconfirm, Tooltip
} from 'antd';
import '../styles/Common.css';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  LockOutlined, TeamOutlined, MenuOutlined, UserOutlined, ApartmentOutlined, SaveOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { API_BASE } from '../config/api';

const { Title, Text } = Typography;
const { Option } = Select;

// Axios 인스턴스 (토큰 자동 부착)
const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* ──────────────────────────────────────────────
   탭1: 역할 관리
────────────────────────────────────────────── */
const RolesTab = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  // 역할명으로부터 자동 역할 코드 생성
  const autoGenerateRoleCode = (roleName) => {
    return (roleName || '')
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[^A-Z0-9_]/g, '')
      .substring(0, 20) || 'ROLE_' + Date.now().toString(36).toUpperCase();
  };

  // 역할명 입력 시 역할 코드 자동 생성
  const handleRoleNameChange = (e) => {
    if (!editing) { // 신규 추가일 때만 자동 생성
      const roleName = e.target.value;
      const roleCode = autoGenerateRoleCode(roleName);
      form.setFieldsValue({ role_code: roleCode });
    }
  };

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/auth/roles');
      const HIDDEN_ROLES = ['admin', 'super_admin'];
      const all = data.data || data;
      setRoles(all.filter(r => !HIDDEN_ROLES.includes(r.role_name)));
    } catch (e) {
      message.error('역할 목록 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const openModal = (role = null) => {
    setEditing(role);
    form.resetFields();
    if (role) {
      form.setFieldsValue({
        role_code: role.role_code,
        role_name: role.role_name,
        description: role.description,
        is_active: role.is_active !== 0,
      });
    } else {
      form.setFieldsValue({ is_active: true });
    }
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    try {
      const payload = {
        role_code: values.role_code,
        role_name: values.role_name,
        description: values.description || '',
        is_active: values.is_active ? 1 : 0,
      };
      console.log('[RolesTab] Sending payload:', payload);
      
      if (editing) {
        await api.put(`/api/auth/roles/${editing.role_id}`, payload);
        message.success('역할이 수정되었습니다.');
      } else {
        await api.post('/api/auth/roles', payload);
        message.success('역할이 생성되었습니다.');
      }
      setModalOpen(false);
      fetchRoles();
    } catch (e) {
      console.error('[RolesTab] Error:', e.response?.data || e.message);
      const errorMsg = e.response?.data?.message || e.message || '오류 발생';
      message.error(`${editing ? '역할 수정' : '역할 생성'} 실패: ${errorMsg}`);
    }
  };

  const handleDelete = async (roleId) => {
    try {
      await api.delete(`/api/auth/roles/${roleId}`);
      message.success('역할이 비활성화되었습니다.');
      fetchRoles();
    } catch (e) {
      message.error('역할 비활성화 실패');
    }
  };

  const columns = [
    { key: 'role_name', title: '역할명', dataIndex: 'role_name', render: (v) => <Text strong>{v}</Text> },
    { key: 'description', title: '설명', dataIndex: 'description' },
    {
      key: 'is_active',
      title: '상태', dataIndex: 'is_active',
      render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? '활성' : '비활성'}</Tag>,
      width: 80
    },
    {
      key: 'action',
      title: '작업', width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="수정">
            <Button size="small" icon={<EditOutlined />} onClick={() => openModal(record)} />
          </Tooltip>
          <Popconfirm
            title="이 역할을 비활성화하시겠습니까?"
            onConfirm={() => handleDelete(record.role_id)}
            okText="예" cancelText="아니오"
          >
            <Tooltip title="비활성화">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>역할 추가</Button>
        <Button icon={<ReloadOutlined />} onClick={fetchRoles}>새로고침</Button>
      </Space>
      <Table
        dataSource={roles}
        columns={columns}
        rowKey="role_id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 15 }}
      />
      <Modal
        title={editing ? '역할 수정' : '역할 추가'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="저장"
        cancelText="취소"
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="role_code" noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="role_name" label="역할명" rules={[{ required: true, message: '역할명을 입력하세요' }]}>
            <Input placeholder="예: 생산부, 관리자, 열람자" onChange={handleRoleNameChange} />
          </Form.Item>
          <Form.Item name="description" label="설명">
            <Input.TextArea rows={2} placeholder="역할 설명" />
          </Form.Item>
          <Form.Item name="is_active" label="활성 여부" valuePropName="checked">
            <Switch checkedChildren="활성" unCheckedChildren="비활성" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

/* ──────────────────────────────────────────────
   탭2: 메뉴 관리
────────────────────────────────────────────── */
const MenusTab = () => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchMenus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/auth/menus');
      setMenus(data.data || data);
    } catch (e) {
      message.error('메뉴 목록 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMenus(); }, [fetchMenus]);

  const openModal = (menu = null) => {
    setEditing(menu);
    form.resetFields();
    if (menu) {
      form.setFieldsValue({
        menu_key: menu.menu_key,
        menu_name: menu.menu_name,
        menu_path: menu.menu_path,
        sort_order: menu.sort_order,
        is_active: menu.is_active !== 0,
      });
    } else {
      form.setFieldsValue({ is_active: true, sort_order: 0 });
    }
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    try {
      const payload = {
        menu_key: values.menu_key,
        menu_name: values.menu_name,
        menu_path: values.menu_path || '',
        sort_order: parseInt(values.sort_order) || 0,
        is_active: values.is_active ? 1 : 0,
      };
      console.log('[MenusTab] Sending payload:', payload);
      
      if (editing) {
        await api.put(`/api/auth/menus/${editing.menu_id}`, payload);
        message.success('메뉴가 수정되었습니다.');
      } else {
        await api.post('/api/auth/menus', payload);
        message.success('메뉴가 생성되었습니다.');
      }
      setModalOpen(false);
      fetchMenus();
    } catch (e) {
      console.error('[MenusTab] Error:', e.response?.data || e.message);
      const errorMsg = e.response?.data?.message || e.message || '오류 발생';
      message.error(`${editing ? '메뉴 수정' : '메뉴 생성'} 실패: ${errorMsg}`);
    }
  };

  const handleDelete = async (menuId) => {
    try {
      await api.delete(`/api/auth/menus/${menuId}`);
      message.success('메뉴가 비활성화되었습니다.');
      fetchMenus();
    } catch (e) {
      message.error('메뉴 비활성화 실패');
    }
  };

  const columns = [
    { key: 'menu_id', title: 'ID', dataIndex: 'menu_id', width: 60 },
    { key: 'menu_key', title: '메뉴 키', dataIndex: 'menu_key', render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
    { key: 'menu_name', title: '메뉴명', dataIndex: 'menu_name', render: (v) => <Text strong>{v}</Text> },
    { key: 'menu_path', title: '경로', dataIndex: 'menu_path' },
    { key: 'sort_order', title: '순서', dataIndex: 'sort_order', width: 70 },
    {
      key: 'is_active',
      title: '상태', dataIndex: 'is_active', width: 80,
      render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? '활성' : '비활성'}</Tag>
    },
    {
      key: 'action',
      title: '작업', width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="수정">
            <Button size="small" icon={<EditOutlined />} onClick={() => openModal(record)} />
          </Tooltip>
          <Popconfirm
            title="이 메뉴를 비활성화하시겠습니까?"
            onConfirm={() => handleDelete(record.menu_id)}
            okText="예" cancelText="아니오"
          >
            <Tooltip title="비활성화">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>메뉴 추가</Button>
        <Button icon={<ReloadOutlined />} onClick={fetchMenus}>새로고침</Button>
      </Space>
      <Table
        dataSource={menus}
        columns={columns}
        rowKey="menu_id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 15 }}
      />
      <Modal
        title={editing ? '메뉴 수정' : '메뉴 추가'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="저장"
        cancelText="취소"
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="menu_key" label="메뉴 키" rules={[{ required: true, message: '메뉴 키를 입력하세요' }]}>
            <Input placeholder="예: attendance, product_report" />
          </Form.Item>
          <Form.Item name="menu_name" label="메뉴명" rules={[{ required: true, message: '메뉴명을 입력하세요' }]}>
            <Input placeholder="예: 야근 보고서" />
          </Form.Item>
          <Form.Item name="menu_path" label="경로">
            <Input placeholder="예: /attendance/report" />
          </Form.Item>
          <Form.Item name="sort_order" label="정렬 순서">
            <Input type="number" min={0} />
          </Form.Item>
          <Form.Item name="is_active" label="활성 여부" valuePropName="checked">
            <Switch checkedChildren="활성" unCheckedChildren="비활성" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

/* ──────────────────────────────────────────────
   탭3: 역할-메뉴 권한 매트릭스
────────────────────────────────────────────── */
const PERM_KEYS = [
  { key: 'can_view', label: '보기' },
  { key: 'can_create', label: '생성' },
  { key: 'can_update', label: '수정' },
  { key: 'can_delete', label: '삭제' },
  { key: 'can_approve', label: '승인' },
  { key: 'can_export', label: '내보내기' },
];

const RoleMenuPermissionsTab = () => {
  const [roles, setRoles] = useState([]);
  const [menus, setMenus] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // localPerms: { [menu_id]: { can_view, can_create, ... } }
  const [localPerms, setLocalPerms] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, menusRes, permsRes] = await Promise.all([
        api.get('/api/auth/roles'),
        api.get('/api/auth/menus'),
        api.get('/api/auth/role-menu-permissions'),
      ]);
      const rolesData = rolesRes.data.data || rolesRes.data;
      const menusData = menusRes.data.data || menusRes.data;
      const permsData = permsRes.data.data || permsRes.data;
      setRoles(rolesData.filter(r => r.is_active !== 0));
      setMenus(menusData.filter(m => m.is_active !== 0));
      setPermissions(permsData);
    } catch (e) {
      message.error('데이터 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // 역할 선택 시 로컬 권한 상태 초기화
  useEffect(() => {
    if (!selectedRole || menus.length === 0) return;
    const init = {};
    menus.forEach(m => {
      const found = permissions.find(
        p => p.role_id === selectedRole && p.menu_id === m.menu_id
      );
      init[m.menu_id] = {
        can_view: found ? !!found.can_view : false,
        can_create: found ? !!found.can_create : false,
        can_update: found ? !!found.can_update : false,
        can_delete: found ? !!found.can_delete : false,
        can_approve: found ? !!found.can_approve : false,
        can_export: found ? !!found.can_export : false,
      };
    });
    setLocalPerms(init);
  }, [selectedRole, permissions, menus]);

  const handleCheck = (menuId, permKey, checked) => {
    setLocalPerms(prev => ({
      ...prev,
      [menuId]: { ...prev[menuId], [permKey]: checked }
    }));
  };

  const handleSaveAll = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      const payloads = menus.map(m => ({
        role_id: selectedRole,
        menu_id: m.menu_id,
        can_view: localPerms[m.menu_id]?.can_view ? 1 : 0,
        can_create: localPerms[m.menu_id]?.can_create ? 1 : 0,
        can_update: localPerms[m.menu_id]?.can_update ? 1 : 0,
        can_delete: localPerms[m.menu_id]?.can_delete ? 1 : 0,
        can_approve: localPerms[m.menu_id]?.can_approve ? 1 : 0,
        can_export: localPerms[m.menu_id]?.can_export ? 1 : 0,
      }));
      console.log('[RoleMenuPermissionsTab] Saving payloads:', payloads);
      
      await Promise.all(
        payloads.map(payload =>
          api.post('/api/auth/role-menu-permissions', payload)
        )
      );
      message.success('권한이 저장되었습니다.');
      fetchAll();
    } catch (e) {
      console.error('[RoleMenuPermissionsTab] Error:', e.response?.data || e.message);
      const errorMsg = e.response?.data?.message || e.message || '오류 발생';
      message.error(`권한 저장 실패: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'menu_name',
      title: '메뉴',
      dataIndex: 'menu_name',
      fixed: 'left',
      width: 160,
      render: (v, record) => (
        <span>
          <Text strong>{v}</Text>
          {record.menu_path && (
            <div>
              <code style={{ fontSize: 11, color: '#888' }}>{record.menu_path}</code>
            </div>
          )}
        </span>
      )
    },
    ...PERM_KEYS.map(({ key, label }) => ({
      key,
      title: label,
      dataIndex: key,
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Checkbox
          checked={!!(localPerms[record.menu_id] && localPerms[record.menu_id][key])}
          disabled={!selectedRole}
          onChange={e => handleCheck(record.menu_id, key, e.target.checked)}
        />
      )
    }))
  ];

  return (
    <Spin spinning={loading}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="역할 선택"
          style={{ minWidth: 200 }}
          value={selectedRole}
          onChange={setSelectedRole}
        >
          {roles.map(r => (
            <Option key={r.role_id} value={r.role_id}>{r.role_name}</Option>
          ))}
        </Select>
        <Button
          type="primary"
          onClick={handleSaveAll}
          disabled={!selectedRole}
          loading={saving}
        >
          변경사항 저장
        </Button>
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>새로고침</Button>
      </Space>
      {!selectedRole && (
        <Text type="secondary">역할을 선택하면 메뉴별 권한을 설정할 수 있습니다.</Text>
      )}
      <Table
        dataSource={menus}
        columns={columns}
        rowKey="menu_id"
        size="small"
        pagination={false}
        scroll={{ x: 700 }}
      />
    </Spin>
  );
};

/* ──────────────────────────────────────────────
   탭4: 사용자 역할 관리
────────────────────────────────────────────── */
const UserRolesTab = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [effectivePerms, setEffectivePerms] = useState([]);
  const [permLoading, setPermLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const fetchBase = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, userRolesRes, usersRes] = await Promise.all([
        api.get('/api/auth/roles'),
        api.get('/api/auth/user-roles'),
        api.get('/api/auth/users'),
      ]);
      const rolesData = (rolesRes.data.data || rolesRes.data).filter(r => r.is_active !== 0);
      const userRolesData = userRolesRes.data.data || userRolesRes.data;
      const usersData = usersRes.data.data || usersRes.data;
      setRoles(rolesData);
      setUserRoles(userRolesData);
      // 전체 사용자(User_TB)를 목록으로 — 역할 미할당 사용자(신규 OAuth 등)도 포함
      setUsers(usersData.map(u => ({
        user_id: u.User_ID,
        user_name: u.User_Name,
        is_oauth: !!u.is_oauth,
      })));
    } catch (e) {
      message.error('데이터 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBase(); }, [fetchBase]);

  const fetchEffective = useCallback(async (userId) => {
    if (!userId) return;
    setPermLoading(true);
    try {
      const { data } = await api.get(`/api/auth/effective-menu-permissions/${userId}`);
      setEffectivePerms(data.data || data);
    } catch (e) {
      setEffectivePerms([]);
    } finally {
      setPermLoading(false);
    }
  }, []);

  const handleUserSelect = (userId) => {
    setSelectedUser(userId);
    fetchEffective(userId);
  };

  const getRoleForUser = (userId) => {
    const ur = userRoles.find(r => r.user_id === userId && r.is_primary);
    return ur ? ur.role_id : null;
  };

  const handleSetRole = async (userId, roleId) => {
    setSaving(true);
    try {
      const payload = { user_id: userId, role_id: roleId };
      console.log('[UserRolesTab] Setting role:', payload);
      
      await api.post('/api/auth/user-roles/primary', payload);
      message.success('역할이 지정되었습니다.');
      fetchBase();
    } catch (e) {
      console.error('[UserRolesTab] Error:', e.response?.data || e.message);
      const errorMsg = e.response?.data?.message || e.message || '오류 발생';
      message.error(`역할 지정 실패: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const userColumns = [
    { key: 'user_name', title: '이름', dataIndex: 'user_name', width: 120, render: (v) => v || '-' },
    { key: 'user_id', title: '사용자 ID', dataIndex: 'user_id', width: 200 },
    {
      key: 'role',
      title: '기본 역할',
      width: 220,
      render: (_, record) => {
        const roleId = getRoleForUser(record.user_id);
        return (
          <Space>
            <Select
              placeholder="역할 선택"
              style={{ width: 150 }}
              value={roleId}
              onChange={rid => handleSetRole(record.user_id, rid)}
              loading={saving}
            >
              {roles.map(r => <Option key={r.role_id} value={r.role_id}>{r.role_name}</Option>)}
            </Select>
            {roleId == null && <Tag color="orange">미할당</Tag>}
          </Space>
        );
      }
    },
    {
      key: 'action',
      title: '유효 권한 확인',
      width: 130,
      render: (_, record) => (
        <Button
          size="small"
          icon={<LockOutlined />}
          onClick={() => handleUserSelect(record.user_id)}
        >
          확인
        </Button>
      )
    }
  ];

  const permColumns = [
    { key: 'menu_name', title: '메뉴명', dataIndex: 'menu_name' },
    { key: 'menu_path', title: '경로', dataIndex: 'menu_path' },
    ...PERM_KEYS.map(({ key, label }) => ({
      key,
      title: label,
      dataIndex: key,
      width: 70,
      align: 'center',
      render: (v) => v
        ? <Tag color="green" style={{ margin: 0 }}>✓</Tag>
        : <Tag color="default" style={{ margin: 0 }}>-</Tag>
    }))
  ];

  const kw = userSearch.trim().toLowerCase();
  const filteredUsers = kw
    ? users.filter(u =>
        String(u.user_id || '').toLowerCase().includes(kw) ||
        String(u.user_name || '').toLowerCase().includes(kw))
    : users;

  return (
    <Spin spinning={loading}>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input.Search
          placeholder="이름 또는 사용자 ID 검색"
          allowClear
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          style={{ width: 280 }}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchBase}>새로고침</Button>
        <Text type="secondary">전체 {users.length}명 / 표시 {filteredUsers.length}명</Text>
      </Space>
      <Table
        dataSource={filteredUsers}
        columns={userColumns}
        rowKey={(record) => String(record.user_id || JSON.stringify(record))}
        size="small"
        pagination={{ pageSize: 15 }}
        style={{ marginBottom: 32 }}
      />
      {selectedUser && (
        <>
          <Title level={5} style={{ marginBottom: 8 }}>
            <LockOutlined /> {selectedUser} 유효 메뉴 권한
          </Title>
          <Spin spinning={permLoading}>
            <Table
              dataSource={effectivePerms}
              columns={permColumns}
              rowKey={(record) => String(record.menu_id || record.menu_key || JSON.stringify(record))}
              size="small"
              pagination={false}
              scroll={{ x: 700 }}
            />
          </Spin>
        </>
      )}
    </Spin>
  );
};

/* ──────────────────────────────────────────────
   탭5: 사용자 부서 (계정 ↔ 담당 부서, 여러 개 가능)
   주간 근무시간 등에서 일반 계정은 여기 지정된 부서만 본다.
────────────────────────────────────────────── */
const ALL_PARTS = '*';

const samePartSet = (a = [], b = []) => a.length === b.length && a.every((x) => b.includes(x));

const UserPartsTab = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userRoleMap, setUserRoleMap] = useState({}); // user_id -> role_id (기본 역할)
  const [partOptions, setPartOptions] = useState([]);
  const [saved, setSaved] = useState({}); // user_id -> [part]
  const [draft, setDraft] = useState({}); // user_id -> [part] (수정 중)
  const [loading, setLoading] = useState(false);
  const [savingUser, setSavingUser] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, userRolesRes, usersRes, userPartsRes, partsRes] = await Promise.all([
        api.get('/api/auth/roles'),
        api.get('/api/auth/user-roles'),
        api.get('/api/auth/users'),
        api.get('/api/auth/user-parts'),
        api.get('/api/partname'),
      ]);
      setRoles((rolesRes.data.data || rolesRes.data).filter((r) => r.is_active !== 0));
      const roleMap = {};
      (userRolesRes.data.data || userRolesRes.data).forEach((ur) => { if (ur.is_primary) roleMap[ur.user_id] = ur.role_id; });
      setUserRoleMap(roleMap);
      setUsers((usersRes.data.data || usersRes.data).map((u) => ({ user_id: u.User_ID, user_name: u.User_Name })));
      const partMap = {};
      (userPartsRes.data.data || userPartsRes.data).forEach((r) => {
        (partMap[r.user_id] = partMap[r.user_id] || []).push(r.part_name);
      });
      setSaved(partMap);
      setDraft({});
      setPartOptions((Array.isArray(partsRes.data) ? partsRes.data : []).map((p) => ({ value: p.partNameEng, label: p.partNameKor })));
    } catch (e) {
      message.error('사용자 부서 데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const currentParts = (userId) => draft[userId] ?? saved[userId] ?? [];

  const handleChange = (userId, values) => {
    // '전체 부서'를 새로 고르면 그것만, 다른 부서를 고르면 '전체'는 해제
    let next = values;
    const prev = currentParts(userId);
    if (values.includes(ALL_PARTS) && !prev.includes(ALL_PARTS)) next = [ALL_PARTS];
    else if (values.length > 1) next = values.filter((v) => v !== ALL_PARTS);
    setDraft((d) => ({ ...d, [userId]: next }));
  };

  const handleSave = async (userId) => {
    setSavingUser(userId);
    try {
      const parts = currentParts(userId);
      await api.put(`/api/auth/user-parts/${encodeURIComponent(userId)}`, { parts });
      setSaved((s) => ({ ...s, [userId]: parts }));
      setDraft((d) => { const n = { ...d }; delete n[userId]; return n; });
      message.success('담당 부서가 저장되었습니다.');
    } catch (e) {
      message.error(e.response?.data?.message || '담당 부서 저장에 실패했습니다.');
    } finally {
      setSavingUser(null);
    }
  };

  const roleName = (roleId) => roles.find((r) => r.role_id === roleId)?.role_name;
  const selectOptions = [{ value: ALL_PARTS, label: '전체 부서' }, ...partOptions];

  const kw = search.trim().toLowerCase();
  const filtered = users.filter((u) => {
    if (roleFilter != null && userRoleMap[u.user_id] !== roleFilter) return false;
    if (!kw) return true;
    return String(u.user_id || '').toLowerCase().includes(kw) || String(u.user_name || '').toLowerCase().includes(kw);
  });

  const columns = [
    { key: 'user_name', title: '이름', dataIndex: 'user_name', width: 110, render: (v) => v || '-' },
    { key: 'user_id', title: '사용자 ID', dataIndex: 'user_id', width: 200 },
    {
      key: 'role',
      title: '역할',
      width: 110,
      render: (_, r) => (roleName(userRoleMap[r.user_id]) ? <Tag>{roleName(userRoleMap[r.user_id])}</Tag> : <Text type="secondary">-</Text>),
    },
    {
      key: 'parts',
      title: '담당 부서',
      render: (_, r) => (
        <Select
          mode="multiple"
          allowClear
          placeholder="부서 선택 (여러 개 가능)"
          style={{ width: '100%', minWidth: 240 }}
          value={currentParts(r.user_id)}
          onChange={(v) => handleChange(r.user_id, v)}
          options={selectOptions}
          optionFilterProp="label"
        />
      ),
    },
    {
      key: 'action',
      title: '',
      width: 90,
      render: (_, r) => (
        <Button
          type="primary"
          size="small"
          icon={<SaveOutlined />}
          loading={savingUser === r.user_id}
          disabled={samePartSet(currentParts(r.user_id), saved[r.user_id] || [])}
          onClick={() => handleSave(r.user_id)}
        >
          저장
        </Button>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        주간 근무시간 화면에서 각 계정이 볼 수 있는 부서를 지정합니다. 한 계정에 여러 부서를 지정할 수 있고,
        <b> '전체 부서'</b>는 모든 부서를 봅니다. 권한 관리 권한이 있는 관리자는 지정 없이도 전체를 봅니다.
      </Text>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          allowClear
          placeholder="역할로 거르기 (예: 생산부)"
          style={{ width: 200 }}
          value={roleFilter}
          onChange={(v) => setRoleFilter(v ?? null)}
        >
          {roles.map((r) => <Option key={r.role_id} value={r.role_id}>{r.role_name}</Option>)}
        </Select>
        <Input.Search
          placeholder="이름 또는 사용자 ID 검색"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 260 }}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>새로고침</Button>
        <Text type="secondary">표시 {filtered.length}명</Text>
      </Space>
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="user_id"
        size="small"
        pagination={{ pageSize: 15 }}
        scroll={{ x: 800 }}
      />
    </Spin>
  );
};

/* ──────────────────────────────────────────────
   메인 페이지
────────────────────────────────────────────── */
const PermissionsPage = () => {
  const tabItems = [
    {
      key: 'roles',
      label: <span><TeamOutlined />역할 관리</span>,
      children: <RolesTab />,
    },
    {
      key: 'menus',
      label: <span><MenuOutlined />메뉴 관리</span>,
      children: <MenusTab />,
    },
    {
      key: 'role-menu',
      label: <span><LockOutlined />역할-메뉴 권한</span>,
      children: <RoleMenuPermissionsTab />,
    },
    {
      key: 'user-roles',
      label: <span><UserOutlined />사용자 역할</span>,
      children: <UserRolesTab />,
    },
    {
      key: 'user-parts',
      label: <span><ApartmentOutlined />사용자 부서</span>,
      children: <UserPartsTab />,
    },
  ];

  return (
    <div className="common-container">
      <Title level={4} style={{ marginBottom: 16 }}>
        <LockOutlined /> 권한 관리
      </Title>
      <Tabs defaultActiveKey="roles" items={tabItems} />
    </div>
  );
};

export default PermissionsPage;
